import React from "react";
import { Button } from "../ui/button";
import * as DocumentPicker from "expo-document-picker";
import { useMutation } from "@tanstack/react-query";
import { useConvexMutation } from "@convex-dev/react-query";
import { api } from "../utils/convex-api";
import Toast from "react-native-toast-message";
import { Id } from "@teachloop/backend/_generated/dataModel";

function StudyMaterialUploader({
  lessonReportId,
}: {
  lessonReportId: Id<"lessonReports">;
}) {
  const {
    mutateAsync: generateStudyMaterialUploadUrl,
    isPending: isGeneratingUploadUrl,
  } = useMutation({
    mutationFn: useConvexMutation(
      api.tutor.lessonReports.mutations.generateStudyMaterialUploadUrl
    ),
  });
  const { mutateAsync: attachStudyMaterial, isPending: isUpdatingUserImage } =
    useMutation({
      mutationFn: useConvexMutation(
        api.tutor.lessonReports.mutations.attachStudyMaterial
      ),
    });
  const { mutateAsync: uploadFile, isPending: isUploadingFile } = useMutation({
    mutationFn: async ({
      uploadUrl,
      file,
      mimeType,
      fileName,
    }: {
      uploadUrl: string;
      file: Blob;
      mimeType: string;
      fileName: string;
    }) => {
      const response = await fetch(uploadUrl, {
        method: "POST",
        body: file,
        headers: {
          "Content-Type": mimeType,
        },
      });
      if (response.ok) {
        const { storageId } = await response.json();
        if (storageId) {
          await attachStudyMaterial({
            storageId,
            fileName,
            lessonReportId: lessonReportId,
          });
        }
      }
    },
  });
  const isPending =
    isGeneratingUploadUrl || isUpdatingUserImage || isUploadingFile;

  const pickStudyMaterial = async () => {
    const result = await DocumentPicker.getDocumentAsync({
      multiple: false,
      type: [
        "application/pdf",
        "application/msword",
        "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
        "application/vnd.ms-excel",
        "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      ],
    });
    const [asset] = result.assets ?? [];
    if (asset) {
      if (!asset.file || !asset.mimeType || !asset.name) {
        Toast.show({ type: "error", text1: "File not found" });
        return;
      }

      const uploadUrl = await generateStudyMaterialUploadUrl({});
      await uploadFile({
        uploadUrl,
        file: asset.file,
        mimeType: asset.mimeType,
        fileName: asset.name,
      });
    }
  };

  return (
    <Button disabled={isPending} size="sm" onPress={pickStudyMaterial} outline className="border">
      <Button.Text size="sm" intent="primary">
        Add study material
      </Button.Text>
    </Button>
  );
}

export default StudyMaterialUploader;
